// /shared/hooks/useBreedSpecificBadge.tsx
import { useCallback } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faWarning } from "@fortawesome/pro-regular-svg-icons";
import { Badge } from "@/shared/components/ui/Badge";
import { Policy } from "../types/Quote.interface";
import { AffectedBreed } from "../components/FullCoverageDetailsModal/coverageData";
import Strings from "../utils/Strings.constants";

// Rate formatting utility
type RateDisplayMode = "percentage" | "multiplier" | undefined;

export const formatRateDisplay = (rate: number, mode?: RateDisplayMode): string => {
    if (mode === "percentage") {
        return `${Math.round((rate - 1) * 100)}%`;
    }

    if (mode === "multiplier") {
        const formatted = Number.isInteger(rate) ? rate : Number(rate.toFixed(1));
        return `${formatted}X`;
    }

    // default (auto)
    if (rate >= 2) {
        const formatted = Number.isInteger(rate) ? rate : Number(rate.toFixed(1));
        return `${formatted}X`;
    }

    return `${Math.round((rate - 1) * 100)}%`;
};

interface UseBreedSpecificBadgeProps {
    isMultiPetLinked?: boolean;
    policies?: Policy[];
    activePetBreed?: string;
    enabled?: boolean;
}

export const useBreedSpecificBadge = ({ isMultiPetLinked, policies, activePetBreed, enabled }: UseBreedSpecificBadgeProps) => {
    const getBreedSpecificBadge = useCallback(
        (affects: AffectedBreed[]) => {
            if (!enabled || !affects) return null;

            let matchingBreed;

            if (isMultiPetLinked) {
                const firstPetBreed = policies?.[0]?.breedName;
                if (firstPetBreed) {
                    matchingBreed = affects.find((affectData: any) => firstPetBreed.includes(affectData.breedName));
                }
            } else {
                matchingBreed = affects.find((affectData: any) => activePetBreed?.includes(affectData.breedName));
            }

            return matchingBreed ? (
                <Badge variant="warning" className="w-fit gap-1 font-medium">
                    <div className="flex size-5 items-center justify-center">
                        <FontAwesomeIcon className="size-4" icon={faWarning} />
                    </div>
                    {formatRateDisplay(matchingBreed.rate, matchingBreed.displayAs)} more likely
                </Badge>
            ) : null;
        },
        [enabled, isMultiPetLinked, policies, activePetBreed]
    );

    const sectionHasBreedConditions = (section: any) => {
        if (!enabled) return false;

        return (
            section.affects?.some((affectData: any) =>
                isMultiPetLinked ? policies?.[0]?.breedName?.includes(affectData.breedName) : activePetBreed?.includes(affectData.breedName)
            ) ||
            section.includes?.some((item: any) => {
                if (!item.affects) return false;

                // Check if any affects match the current pet breed logic
                if (isMultiPetLinked) {
                    const firstPetBreed = policies?.[0]?.breedName;
                    return firstPetBreed && item.affects.some((affectData: any) => firstPetBreed.includes(affectData.breedName));
                } else {
                    return item.affects.some((affectData: any) => activePetBreed?.includes(affectData.breedName));
                }
            })
        );
    };

    const getPluralBreedName = (breedName: string): string => {
        const mapping = Strings.PTZ_US.BREED_DISPLAY_MAPPINGS.find(mapping => breedName.includes(mapping.breedLabel));
        return mapping ? mapping.pluralLabel : breedName;
    };

    const getDisplayBreedName = useCallback(() => {
        if (!enabled) return undefined;
        const breedName: string | undefined = isMultiPetLinked ? policies?.[0]?.breedName : activePetBreed;
        if (!breedName) return undefined;
        return getPluralBreedName(breedName);
    }, [enabled, isMultiPetLinked, policies, activePetBreed]);

    const getFilteredAccidentItems = useCallback(
        (allItems: any[]) => {
            if (!enabled) {
                // If disabled, show only default items (no breed-specific filtering)
                return allItems.filter(item => !item.replaces);
            }

            const targetBreed = isMultiPetLinked ? policies?.[0]?.breedName : activePetBreed;
            if (!targetBreed) {
                // No breed - show only defaults (items without replaces property)
                return allItems.filter(item => !item.replaces);
            }

            // Get breed-specific items that match current breed (items with replaces property)
            const matchingBreedItems = allItems.filter(item => {
                if (!item.affects || !item.replaces) return false; // Must have both affects AND replaces
                return item.affects.some((affectData: any) => targetBreed.includes(affectData.breedName));
            });

            // Get IDs of items being replaced
            const replacedIds = matchingBreedItems.filter(item => item.replaces).flatMap(item => item.replaces);

            // Get default items (items without replaces property) and exclude replaced ones
            const defaultItems = allItems.filter(item => !item.replaces && !replacedIds.includes(item.id));

            // Combine defaults + breed-specific items
            return [...defaultItems, ...matchingBreedItems];
        },
        [enabled, isMultiPetLinked, policies, activePetBreed]
    );

    return { getBreedSpecificBadge, sectionHasBreedConditions, getDisplayBreedName, getFilteredAccidentItems };
};
